/**
 * Emission Factors Page
 *
 * Browse and manage emission factors from various sources.
 * Based on CarbonIQ Emission Factors design pattern.
 */

import React, { useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronRight, ChevronDown, Download, Upload, FileSpreadsheet, Check, X, Loader2, Info, CheckCircle, AlertTriangle, Trash2, Database } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@g17eco/ui-modern/components/ui/dropdown-menu';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { DeleteModal } from '@g17eco/molecules/confirm-modal';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { NoData } from '@g17eco/molecules/no-data';
import { BasicAlert } from '@g17eco/molecules/alert/BasicAlert';
import { DebouncedSearchBox } from '@g17eco/molecules/search/DebouncedSearchBox';
import { SelectFactory } from '@g17eco/molecules/select/SelectFactory';
import { Option, SelectTypes } from '@g17eco/molecules/select/types';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { downloadBlob } from '@utils/downloadBlob';
import {
  useGetEmissionFactorsQuery,
  useLazyGetFactorImportTemplateQuery,
  useImportFactorsFromExcelMutation,
  useDeleteEmissionFactorMutation,
} from '../api/emissions';
import { EmissionFactorProvider } from '../types/emissions';
import { SCOPE_TAILWIND } from '../config/scopeBranding';
import { getActivityUnitLabel } from '../utils/activityUtils';
import type {
  EmissionFactor,
  EmissionScope,
  EmissionFactorSourceDefinition,
  EmissionFactorsListResponse,
  EmissionFactorImportResult,
  RegionOption,
} from '../types/emissions';

interface FactorSource {
  name: string;
  count: number;
  color: string;
}

const SCOPE_LABELS: Record<EmissionScope, string> = {
  scope1: 'Scope 1',
  scope2: 'Scope 2',
  scope3: 'Scope 3',
};


const ALL_OPTION = 'all';

// Helper to get label from source definitions
const getSourceLabel = (
  source: string,
  sourceDefinitions: EmissionFactorSourceDefinition[]
): string => {
  return sourceDefinitions.find((s) => s.value === source)?.label ?? source;
};

const formatActivityType = (activityType: string): string =>
  activityType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const DetailField = ({ label, value }: { label: string; value: string }) => (
  <div>
    <span className='tw:text-muted-foreground'>{label}: </span>
    <span className='tw:font-medium'>{value}</span>
  </div>
);

const FactorDetails = ({ factor }: { factor: EmissionFactor }) => (
  <div className='tw:px-4 tw:py-3 tw:bg-muted/30'>
    {factor.description && (
      <p className='tw:text-sm tw:text-muted-foreground tw:mb-2'>{factor.description}</p>
    )}
    <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-3 tw:gap-2 tw:text-sm'>
      <DetailField label='Activity Type' value={formatActivityType(factor.activityType)} />
      <DetailField label='Activity Unit' value={getActivityUnitLabel(factor.activityUnit)} />
      <DetailField label='GWP' value={factor.gwpMethodology} />
      {factor.fuelType && <DetailField label='Fuel Type' value={formatActivityType(factor.fuelType)} />}
      {factor.subcategory && <DetailField label='Subcategory' value={factor.subcategory} />}
      {factor.co2Factor != null && <DetailField label='CO2' value={`${factor.co2Factor} ${factor.factorUnit}`} />}
      {factor.ch4Factor != null && <DetailField label='CH4' value={`${factor.ch4Factor} ${factor.factorUnit}`} />}
      {factor.n2oFactor != null && <DetailField label='N2O' value={`${factor.n2oFactor} ${factor.factorUnit}`} />}
    </div>
  </div>
);

const SourceStatsCard = ({ source }: { source: FactorSource }) => {
  return (
    <Card className='tw:h-full'>
      <CardContent className='tw:p-4 tw:text-center'>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-1'>{source.name}</p>
        <h3 className='tw:text-2xl tw:font-bold tw:mb-0'>{source.count.toLocaleString()}</h3>
      </CardContent>
    </Card>
  );
};

interface FilterState {
  searchQuery: string;
  sourceFilter: string;
  regionFilter: string;
  categoryFilter: string;
}

interface EmissionFactorsContentProps {
  data: EmissionFactorsListResponse;
  filters: FilterState;
  onFilterChange: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  onDeleteFactor: (factor: EmissionFactor) => void;
}

const buildSourceOptions = (sources: EmissionFactorSourceDefinition[]): Option<string>[] => [
  { label: 'All Sources', value: ALL_OPTION },
  ...sources.map((s) => ({ label: s.label, value: s.value })),
];

const buildRegionOptions = (regions: RegionOption[]): Option<string>[] => [
  { label: 'All Regions', value: ALL_OPTION },
  ...regions.map((r) => ({ label: r.name, value: r.code })),
];

const buildCategoryOptions = (categories: string[]): Option<string>[] => [
  { label: 'All Categories', value: ALL_OPTION },
  ...categories.map((c) => ({ label: c, value: c })),
];

const EmissionFactorsContent = ({ data, filters, onFilterChange, onDeleteFactor }: EmissionFactorsContentProps) => {
  const { searchQuery, sourceFilter, regionFilter, categoryFilter } = filters;
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (factorId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(factorId)) {
        next.delete(factorId);
      } else {
        next.add(factorId);
      }
      return next;
    });
  };

  const factors = useMemo(() => data.emissionFactors ?? [], [data.emissionFactors]);
  const sourceDefinitions = useMemo(() => data.sources ?? [], [data.sources]);
  const regions = useMemo(() => data.regions ?? [], [data.regions]);
  const categories = useMemo(() => data.categories ?? [], [data.categories]);

  // Build select options
  const sourceOptions = useMemo(() => buildSourceOptions(sourceDefinitions), [sourceDefinitions]);
  const regionOptions = useMemo(() => buildRegionOptions(regions), [regions]);
  const categoryOptions = useMemo(() => buildCategoryOptions(categories), [categories]);

  // Filter factors locally for display
  const filteredFactors = useMemo(() => {
    return factors.filter((factor: EmissionFactor) => {
      const matchesSearch =
        !searchQuery || factor.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSource = sourceFilter === ALL_OPTION || factor.source === sourceFilter;
      const matchesCategory = categoryFilter === ALL_OPTION || factor.category === categoryFilter;
      return matchesSearch && matchesSource && matchesCategory;
    });
  }, [factors, searchQuery, sourceFilter, categoryFilter]);

  // Calculate source stats from filtered factors using source definitions
  const sourceStats = useMemo((): FactorSource[] => {
    const stats: Record<string, number> = { total: filteredFactors.length };

    // Initialize counts for all known sources
    sourceDefinitions.forEach((source) => {
      stats[source.value] = 0;
    });

    // Count factors by source
    filteredFactors.forEach((factor) => {
      if (factor.source in stats) {
        stats[factor.source]++;
      }
    });

    // Build stats array with labels from API
    const result: FactorSource[] = [
      { name: 'Total Factors', count: stats.total, color: 'primary' },
    ];

    sourceDefinitions.forEach((source) => {
      if (source.value !== 'custom') {
        result.push({
          name: source.label,
          count: stats[source.value] ?? 0,
          color: 'secondary',
        });
      }
    });

    // Add custom at the end
    const customSource = sourceDefinitions.find((s) => s.value === 'custom');
    if (customSource) {
      result.push({
        name: customSource.label,
        count: stats.custom ?? 0,
        color: 'secondary',
      });
    }

    return result;
  }, [filteredFactors, sourceDefinitions]);

  return (
    <>
      {/* Source Stats */}
      <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-4 tw:gap-3 tw:mb-6'>
        {sourceStats.map((source) => (
          <SourceStatsCard key={source.name} source={source} />
        ))}
      </div>

      {/* Factor Library */}
      <Card className='tw:mb-6'>
        <CardContent className='tw:p-4'>
          {/* Header */}
          <div className='tw:flex tw:justify-between tw:items-center tw:mb-4'>
            <div>
              <h5 className='tw:font-semibold tw:mb-1 tw:flex tw:items-center tw:gap-2'>
                <Database className='tw:size-4 tw:text-muted-foreground' />
                Factor Library
              </h5>
              <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
                Browse and manage emission factors from EPA, DEFRA, IEA, and custom sources
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className='tw:flex tw:flex-wrap tw:gap-2 tw:mb-4'>
            <div className='tw:flex-grow tw:min-w-[200px] tw:max-w-[400px]'>
              <DebouncedSearchBox
                placeholder='Search factors...'
                defaultText={searchQuery}
                onTextChange={(value) => onFilterChange('searchQuery', value)}
                wait={300}
              />
            </div>
            <div className='tw:min-w-[150px]'>
              <SelectFactory
                selectType={SelectTypes.SingleSelect}
                options={sourceOptions}
                value={sourceOptions.find((o) => o.value === sourceFilter) ?? null}
                onChange={(option) => onFilterChange('sourceFilter', option?.value ?? ALL_OPTION)}
                isSearchable={false}
              />
            </div>
            <div className='tw:min-w-[150px]'>
              <SelectFactory
                selectType={SelectTypes.SingleSelect}
                options={regionOptions}
                value={regionOptions.find((o) => o.value === regionFilter) ?? null}
                onChange={(option) => onFilterChange('regionFilter', option?.value ?? ALL_OPTION)}
              />
            </div>
            <div className='tw:min-w-[180px]'>
              <SelectFactory
                selectType={SelectTypes.SingleSelect}
                options={categoryOptions}
                value={categoryOptions.find((o) => o.value === categoryFilter) ?? null}
                onChange={(option) => onFilterChange('categoryFilter', option?.value ?? ALL_OPTION)}
              />
            </div>
          </div>

          {/* Table */}
          {filteredFactors.length === 0 ? (
            <div className='tw:text-center tw:py-12 tw:text-muted-foreground'>
              <Database className='tw:size-12 tw:opacity-25 tw:mx-auto tw:mb-3' />
              <p className='tw:mb-1'>No emission factors found</p>
              <p className='tw:mb-0 tw:text-sm'>
                Try adjusting your search or filter criteria.
              </p>
            </div>
          ) : (
            <div className='tw:overflow-x-auto'>
              <table className='tw:w-full tw:text-sm'>
                <thead className='tw:bg-muted/50'>
                  <tr>
                    <th className='tw:text-left tw:px-3 tw:py-2 tw:font-medium'>Name</th>
                    <th className='tw:text-left tw:px-3 tw:py-2 tw:font-medium'>Scope</th>
                    <th className='tw:text-left tw:px-3 tw:py-2 tw:font-medium'>Factor</th>
                    <th className='tw:text-left tw:px-3 tw:py-2 tw:font-medium'>Source</th>
                    <th className='tw:text-left tw:px-3 tw:py-2 tw:font-medium'>Region</th>
                    <th className='tw:text-left tw:px-3 tw:py-2 tw:font-medium'>Status</th>
                    <th className='tw:w-10'></th>
                  </tr>
                </thead>
                <tbody className='tw:divide-y'>
                  {filteredFactors.map((factor: EmissionFactor) => {
                    const isExpanded = expandedRows.has(factor._id);
                    return (
                      <React.Fragment key={factor._id}>
                        <tr
                          onClick={() => toggleRow(factor._id)}
                          className='tw:cursor-pointer tw:hover:bg-muted/30'
                        >
                          <td className='tw:px-3 tw:py-2'>
                            <div className='tw:flex tw:items-center tw:gap-2'>
                              {isExpanded ? (
                                <ChevronDown className='tw:size-3.5 tw:text-muted-foreground tw:shrink-0' />
                              ) : (
                                <ChevronRight className='tw:size-3.5 tw:text-muted-foreground tw:shrink-0' />
                              )}
                              <div>
                                <div className='tw:flex tw:items-center tw:gap-1.5'>
                                  <span className='tw:font-medium'>{factor.name}</span>
                                  {factor.provider === EmissionFactorProvider.Manual && (
                                    <Badge variant='secondary' className='tw:text-xs tw:font-normal'>
                                      Custom
                                    </Badge>
                                  )}
                                </div>
                                <div className='tw:text-xs tw:text-muted-foreground'>{factor.category}</div>
                              </div>
                            </div>
                          </td>
                          <td className='tw:px-3 tw:py-2'>
                            <Badge className={cn(SCOPE_TAILWIND[factor.scope]?.iconBg ?? 'tw:bg-muted', SCOPE_TAILWIND[factor.scope]?.icon ?? 'tw:text-muted-foreground')}>
                              {SCOPE_LABELS[factor.scope] ?? factor.scope}
                            </Badge>
                          </td>
                          <td className='tw:px-3 tw:py-2 tw:text-xs'>
                            {factor.factor.toFixed(4)} {factor.factorUnit}
                          </td>
                          <td className='tw:px-3 tw:py-2'>
                            <div>{getSourceLabel(factor.source, sourceDefinitions)}</div>
                            <div className='tw:text-xs tw:text-muted-foreground'>{factor.year}</div>
                          </td>
                          <td className='tw:px-3 tw:py-2'>{factor.regionName}</td>
                          <td className='tw:px-3 tw:py-2'>
                            <Badge
                              className={cn(
                                factor.isActive
                                  ? 'tw:bg-emerald-500/10 tw:text-emerald-700 tw:border-emerald-200'
                                  : 'tw:bg-muted tw:text-muted-foreground'
                              )}
                            >
                              {factor.isActive ? (
                                <Check className='tw:size-3 tw:mr-1' />
                              ) : (
                                <X className='tw:size-3 tw:mr-1' />
                              )}
                              {factor.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className='tw:px-2 tw:py-2 tw:text-center'>
                            {factor.provider === EmissionFactorProvider.Manual && (
                              <Button
                                variant='ghost'
                                size='icon'
                                title='Delete custom factor'
                                className='tw:size-7 tw:text-destructive tw:hover:text-destructive/80'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteFactor(factor);
                                }}
                              >
                                <Trash2 className='tw:size-3.5' />
                              </Button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className='tw:p-0'>
                              <FactorDetails factor={factor} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <BasicAlert type='info'>
        <Info className='tw:inline tw:size-4 tw:mr-2' />
        <strong>About Emission Factors</strong>
        <p className='tw:mb-0 tw:mt-1'>
          Emission factors convert activity data into CO2 equivalent emissions. We support
          multiple global databases including EPA (US), DEFRA (UK), IEA (International).
        </p>
      </BasicAlert>
    </>
  );
};

export const EmissionFactorsPage = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    sourceFilter: ALL_OPTION,
    regionFilter: ALL_OPTION,
    categoryFilter: ALL_OPTION,
  });
  const [importResult, setImportResult] = useState<EmissionFactorImportResult | null>(null);
  const [factorToDelete, setFactorToDelete] = useState<EmissionFactor | null>(null);

  const handleFilterChange = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Fetch emission factors and sources from API
  const factorsQuery = useGetEmissionFactorsQuery({
    initiativeId: initiativeId ?? '',
    query: filters.searchQuery || undefined,
    source: filters.sourceFilter !== ALL_OPTION ? filters.sourceFilter : undefined,
    region: filters.regionFilter !== ALL_OPTION ? filters.regionFilter : undefined,
    limit: 100,
  });

  // Template download and import mutations
  const [getTemplate, { isFetching: isDownloadingTemplate }] = useLazyGetFactorImportTemplateQuery();
  const [importFactors, { isLoading: isImporting }] = useImportFactorsFromExcelMutation();
  const [deleteFactor] = useDeleteEmissionFactorMutation();

  const handleDownloadTemplate = async () => {
    if (!initiativeId) {
      return;
    }
    const result = await getTemplate({ initiativeId });
    if (result.data) {
      downloadBlob(result.data, 'emission-factors-template.xlsx');
    } else if (result.error) {
      toastFromError({ error: result.error, title: 'Template download failed' });
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !initiativeId) {
      return;
    }

    setImportResult(null);

    const result = await importFactors({ initiativeId, file }).unwrap()
      .catch((error: unknown) => {
        toastFromError({ error, title: 'Failed to import emission factors' });
        return undefined;
      });

    if (result) {
      setImportResult(result);
      generateToast({
        color: result.summary.failed === 0 ? 'success' : 'warning',
        message: result.summary.failed === 0
          ? `Successfully imported ${result.summary.successful} emission factor${result.summary.successful !== 1 ? 's' : ''}`
          : `Imported ${result.summary.successful} factor${result.summary.successful !== 1 ? 's' : ''}, ${result.summary.failed} failed`,
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const dismissImportResult = () => {
    setImportResult(null);
  };

  const handleDeleteClick = (factor: EmissionFactor) => {
    setFactorToDelete(factor);
  };

  const handleConfirmDelete = () => {
    if (!factorToDelete || !initiativeId) {
      return;
    }
    deleteFactor({ initiativeId, factorId: factorToDelete._id })
      .unwrap()
      .then(() => {
        generateToast({
          color: 'success',
          message: `Emission factor "${factorToDelete.name}" deleted`,
        });
      })
      .catch((error: unknown) => {
        toastFromError({ error, title: 'Failed to delete emission factor' });
      })
      .finally(() => {
        setFactorToDelete(null);
      });
  };

  return (
    <Dashboard>
      {/* Hidden file input for import */}
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        onChange={handleFileSelected}
      />

      {/* Page Header */}
      <div className='tw:flex tw:justify-between tw:items-center tw:mb-6'>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Emission Factors</h4>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
            Manage emission factors from global databases and custom sources
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              disabled={isDownloadingTemplate || isImporting}
              className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
            >
              {isImporting ? (
                <>
                  <Loader2 className='tw:size-4 tw:animate-spin' />
                  Importing...
                </>
              ) : (
                <>
                  <FileSpreadsheet className='tw:size-4' />
                  Import
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onClick={handleDownloadTemplate} disabled={isDownloadingTemplate}>
              <Download className='tw:size-4' />
              {isDownloadingTemplate ? 'Downloading...' : 'Download Template'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleImportClick} disabled={isImporting}>
              <Upload className='tw:size-4' />
              Import from Excel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Import Progress */}
      {isImporting && (
        <div className='tw:flex tw:gap-3 tw:rounded-lg tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-4 tw:text-blue-800 tw:mb-6 tw:text-sm'>
          <Loader2 className='tw:size-4 tw:animate-spin tw:shrink-0 tw:mt-0.5' />
          <div>
            <span>Importing emission factors...</span>
            <div className='tw:h-1 tw:w-full tw:rounded-full tw:bg-blue-200 tw:mt-2 tw:overflow-hidden'>
              <div className='tw:h-full tw:rounded-full tw:bg-blue-500 tw:animate-pulse tw:w-full' />
            </div>
          </div>
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div
          className={cn(
            'tw:rounded-lg tw:border tw:p-4 tw:mb-6 tw:text-sm',
            importResult.summary.failed === 0
              ? 'tw:border-emerald-200 tw:bg-emerald-50 tw:text-emerald-800'
              : 'tw:border-amber-200 tw:bg-amber-50 tw:text-amber-800'
          )}
        >
          <div className='tw:flex tw:items-start tw:justify-between'>
            <div className='tw:flex tw:gap-3'>
              {importResult.summary.failed === 0 ? (
                <CheckCircle className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
              ) : (
                <AlertTriangle className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
              )}
              <div>
                <strong>Import Complete</strong>
                <div className='tw:mt-1 tw:flex tw:gap-4'>
                  <span className='tw:flex tw:items-center tw:gap-1'>
                    <Check className='tw:size-3' />
                    {importResult.summary.successful} successful
                  </span>
                  {importResult.summary.failed > 0 && (
                    <span className='tw:flex tw:items-center tw:gap-1'>
                      <X className='tw:size-3' />
                      {importResult.summary.failed} failed
                    </span>
                  )}
                </div>
                {importResult.summary.failed > 0 && (
                  <div className='tw:mt-2'>
                    <strong>Errors:</strong>
                    <ul className='tw:mb-0 tw:mt-1 tw:space-y-0.5 tw:list-disc tw:list-inside'>
                      {importResult.results
                        .filter((r) => !r.success && r.errors)
                        .slice(0, 5)
                        .map((r) => (
                          <li key={r.row}>
                            Row {r.row}: {r.errors?.join(', ')}
                          </li>
                        ))}
                      {importResult.results.filter((r) => !r.success).length > 5 && (
                        <li>...and {importResult.results.filter((r) => !r.success).length - 5} more errors</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <Button
              variant='ghost'
              size='icon'
              aria-label='Close'
              onClick={dismissImportResult}
            >
              ×
            </Button>
          </div>
        </div>
      )}

      <QueryWrapper
        query={factorsQuery}
        onNoData={() => <NoData text='No emission factors available.' />}
        onError={() => <NoData text='There was an error loading emission factors. Please try again.' />}
        onSuccess={(data) => (
          <EmissionFactorsContent
            data={data}
            filters={filters}
            onFilterChange={handleFilterChange}
            onDeleteFactor={handleDeleteClick}
          />
        )}
      />

      <DeleteModal
        title='Emission Factor'
        content={
          factorToDelete
            ? `Are you sure you want to delete "${factorToDelete.name}"? This action cannot be undone.`
            : ''
        }
        isOpen={factorToDelete !== null}
        handleDelete={handleConfirmDelete}
        handleCancel={() => setFactorToDelete(null)}
      />
    </Dashboard>
  );
};
